import React, { useReducer, useRef, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { findDOMNode } from 'react-dom';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';

import {
  VideoWrapper,
  VideoControls,
  Slider,
  Track,
  Thumb,
  VideoTime,
  ControlOptions,
  VideoPlayPause,
  CtrlOptionsIcons,
  ToggleIcons
} from './elements';
import { VideoReducer, VideoPlayerState } from './state-reducer';
import { formatSeconds } from 'utils/stringUtil';

import Spinner from 'components/spinner';
import CountDown from './CountDown';

import Play from 'assets/icons/video-play-button.svg';
import Pause from 'assets/icons/pause-video-icon.svg';
import { ReactComponent as FullScreen } from 'assets/icons/full-screen.svg';
import { ReactComponent as Settings } from 'assets/icons/settings-icon.svg';
import { ReactComponent as Captions } from 'assets/icons/closed-captions.svg';
import { ReactComponent as Theater } from 'assets/icons/theater-mode.svg';

import useDoubleClick from 'utils/hooks/useDoubleClick';
import { useWindowSize } from 'utils/hooks';
import useToast from 'components/toast';
import Loader from 'components/loader';

function VideoPlayer({ style, streams, res, ...props }) {
  /**
   * This Component needs rebuild. Make changes with caution
   */
  const videoPlayRef = useRef();
  const { width } = useWindowSize();
  const [state, dispatch] = useReducer(VideoReducer, VideoPlayerState);
  const {
    isBuffering,
    playing,
    duration,
    playedSeconds,
    played,
    seeking
  } = state;

  const toast = useToast();

  const timeDiffAfterStart = Math.floor(
    ((+new Date() - +new Date(res?.starting_time)) / 1000 / 60) * 60
  );

  let seekTOTime =
    timeDiffAfterStart >= duration ? duration : timeDiffAfterStart;

  // let isLive =
  //   res.isLive ||
  //   res.id === '9bc5e096-bbf8-4097-8377-cdb5157bb60c' ||
  //   res.id === '788a831c-6947-4dfe-8726-ca95b80b910c';

  useEffect(() => {
    // if (!isLive) {
    // }
    if (!duration || timeDiffAfterStart >= duration) {
      dispatch({ type: 'SET_VIDEO_ENDED' });
    } else if (timeDiffAfterStart) {
      !playing && dispatch({ type: 'SET_VIDEO_PLAY' });
      duration &&
        videoPlayRef.current &&
        videoPlayRef.current.seekTo(seekTOTime, 'seconds');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);
  const handleSeekChange = e => {
    dispatch({ type: 'SET_VIDEO_PLAYED', payload: parseFloat(e.target.value) });
  };

  const handleSeekMouseDown = () => {
    dispatch({ type: 'SET_VIDEO_SEEKING', payload: true });
  };

  const handleSeekMouseUp = e => {
    videoPlayRef.current.seekTo(parseFloat(e.target.value));
    dispatch({ type: 'SET_VIDEO_SEEKING', payload: false });
  };

  const handleOnProgress = progress => {
    if (!seeking) {
      dispatch({
        type: 'SET_VIDEO_PLAYED',
        payload: progress.played
      });
      dispatch({
        type: 'SET_VIDEO_PLAYED_SECONDS',
        payload: progress.playedSeconds
      });
    }
  };

  const handleClickFullscreen = () => {
    screenfull.request(findDOMNode(videoPlayRef.current));
  };

  // const handleExistFullScreen = () => {
  //   screenfull.exit(findDOMNode(videoPlayRef.current));
  // };

  const [refCallback] = useDoubleClick(handleClickFullscreen);

  const handleOnEnded = () => {
    dispatch({ type: 'SET_VIDEO_ENDED' });
    toast({
      title: 'Ended!',
      description: 'Video has Ended',
      status: 'success',
      position: 'bottom-left',
      duration: 5000,
      isClosable: true
    });
  };

  const handleOnKeyPress = e => {
    if (e.key === 'f' || e.key === 'F') {
      handleClickFullscreen();
    }
  };

  const handlePlayPause = () => {
    dispatch({ type: 'TOGGLE_VIDEO_PLAY_PAUSE' });
  };

  const handlePlay = () => {
    dispatch({ type: 'SET_VIDEO_PLAY' });
  };

  const handleOnPause = () => {
    dispatch({ type: 'SET_VIDEO_PAUSED' });
  };

  const toggleButtons = () => {
    return (
      <ToggleIcons
        src={playing ? Pause : Play}
        alt={playing ? 'PauseIcon' : 'PlayIcon'}
      />
    );
  };

  const FarRightControls = [
    { icon: <Captions key="Captions-control" />, type: 'SET_VIDEO_ENDED' },
    { icon: <Theater key="theater-control" />, type: 'SET_ENDED' },
    { icon: <Settings key="settings-control" />, type: 'SET_ENDED' },
    {
      icon: <FullScreen key="screen-control" onClick={handleClickFullscreen} />
    }
  ];

  let now = new Date().getTime();

  const eventPath = `/stream/:id`;

  const mookhUrl = streams ? streams[0].trailer_url : res.stream_url;

  const timeDifferenceToVideo = +new Date(res?.starting_time) - +new Date();

  const duringLiveStream =
    now > new Date(res?.starting_time) && now < new Date(res?.ending_time);

  // const afterLive = +new Date(res?.starting_time) + duration;

  return (
    <>
      <VideoWrapper ref={refCallback} style={style} {...props}>
        {timeDifferenceToVideo > 0 && props.match.path === eventPath ? (
          <CountDown startTime={res?.starting_time} />
        ) : (
          <>
            <ReactPlayer
              ref={videoPlayRef}
              className="video-player"
              width="100%"
              height={
                width > 768 && props.match.path === eventPath ? '600px' : '100%'
              }
              url={mookhUrl}
              controls={false}
              playing={playing}
              onClick={handlePlayPause}
              onReady={() =>
                dispatch({
                  type: 'SET_VIDEO_BUFFERING',
                  payload: false
                })
              }
              onPlay={handlePlay}
              onPause={handleOnPause}
              onBuffer={() =>
                dispatch({
                  type: 'SET_VIDEO_BUFFERING',
                  payload: true
                })
              }
              onBufferEnd={() =>
                dispatch({
                  type: 'SET_VIDEO_BUFFERING',
                  payload: false
                })
              }
              onProgress={handleOnProgress}
              onDuration={duration =>
                dispatch({
                  type: 'SET_VIDEO_DURATION',
                  payload: duration
                })
              }
              onKeyPress={handleOnKeyPress}
              onEnded={handleOnEnded}
            />
            <VideoControls>
              <VideoTime>
                <span>{formatSeconds(playedSeconds)}</span>
                <span>{formatSeconds(duration)}</span>
              </VideoTime>
              <Slider>
                <Thumb
                  type="range"
                  min={0}
                  max={1}
                  step="any"
                  value={played}
                  onChange={handleSeekChange}
                  onMouseDown={handleSeekMouseDown}
                  onMouseUp={handleSeekMouseUp}
                  disabled={duringLiveStream && props.match.path === eventPath}
                />
                <Track max={1} value={played} />
              </Slider>
              <ControlOptions>
                {isBuffering ? (
                  <Loader>
                    <Spinner />
                  </Loader>
                ) : (
                  <VideoPlayPause onClick={handlePlayPause}>
                    {toggleButtons()}
                  </VideoPlayPause>
                )}
                <CtrlOptionsIcons>
                  {FarRightControls.map((control, i) => control.icon)}
                </CtrlOptionsIcons>
              </ControlOptions>
            </VideoControls>
          </>
        )}
      </VideoWrapper>
    </>
  );
}

export default withRouter(VideoPlayer);
